import { ChangeDetectionStrategy, Component, HostBinding, HostListener, computed, signal, input, output, viewChild } from '@angular/core';
import { Config } from '@environments/config';
import { Resources } from '@local/client-contracts';
import { Constants } from '@local/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FlagsService } from '@shared/services/flags.service';
import { isProdEnv } from '@shared/utils';
import { stopEvent } from '@shared/utils/elements-util';
import { AnswerSearchService } from 'src/app/bar/services/answers-search.service';
import { SortService } from 'src/app/bar/services/sort.service';
import { AnswerCompletionTypes, AnswerSearchItem } from '../../models/results-types';
import { AnswerActionsComponent } from '../answer-actions/answer-actions.component';
import { isExtension } from '@local/common-web';
import { ClientStorageService } from '@shared/services/client-storage.service';
import { TypingViewMode } from '@local/ui-infra';
import { AnswerConstants } from './answer.constants';
@UntilDestroy()
@Component({
  selector: 'result-answer-item',
  templateUrl: './result-answer-item.component.html',
  styleUrls: ['./base-answer.scss', './result-answer-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultsAnswerItemComponent {
  private isExtension = isExtension();

  readonly NO_ANSWER_TEXT = AnswerConstants.NO_ANSWER_TEXT;
  readonly NO_ANSWER_TEXT_LOOK_UP = AnswerConstants.NO_ANSWER_TEXT_LOOK_UP;

  isHovered = signal(false);
  contextMenuActive = signal(false);
  resourcePopupActiveIndex = signal<number | null>(null);
  enableDisclaimer = signal(false);
  debugEnv = !isProdEnv();
  answerTypingFinished = signal(false);

  model = input<AnswerSearchItem | null>(null);
  sessionId = input<string>();
  hideFollowUp = input<boolean>();
  isFeedbackOpen = input<boolean>();

  onFollowUpChat = output();
  feedbackShow = output<Resources.FeedbackType>();

  answerActionsComponent = viewChild<AnswerActionsComponent>(AnswerActionsComponent);

  isCompleteAnswer = computed(() => AnswerCompletionTypes.includes(this.model()?.state) || this.isResourceLookup());

  isResourceLookup = computed(() => this.model()?.intent === 'ResourceLookup');

  showResourcesNumbers = computed(() => this.model()?.intent !== 'People' && !!this.model().references?.length);

  typingViewMode = computed<TypingViewMode>(() =>
    (this.isResourceLookup() && this.model()?.state !== 'Cache') || AnswerConstants.DO_TYPING.includes(this.model()?.state)
      ? 'interactive'
      : 'static'
  );

  @HostListener('contextmenu', ['$event'])
  async onRightClick(event: MouseEvent) {
    const selectedText = window.getSelection()?.toString().trim();
    if (selectedText) {
      this.answerActionsComponent().openContextMenu(event, selectedText);
      this.contextMenuActive.set(true);
      stopEvent(event);
    }
  }

  @HostBinding('attr.isExtensionMode') get isExtensionMode() {
    return this.isExtension;
  }

  constructor(
    public sortService: SortService,
    private flagsService: FlagsService,
    private answerSearchService: AnswerSearchService,
    private clientStorage: ClientStorageService
  ) {
    this.answerSearchService.resourcePopupOpen$.pipe(untilDestroyed(this)).subscribe((res) => {
      this.resourcePopupActiveIndex.set(res?.index);
    });
    this.initFlags();
  }

  private async initFlags() {
    this.flagsService.all$.pipe(untilDestroyed(this)).subscribe(async () => {
      this.enableDisclaimer.set(await this.flagsService.isEnabled(Constants.ENABLE_ANSWER_DISCLAIMER));
    });
    const hideSearchId = ((await this.clientStorage.get('developerFlags')) as any)?.hideSearchId?.value;
    this.debugEnv = !isProdEnv() && !hideSearchId;
  }

  openSearchLog(searchId: string) {
    window.open(`${Config.downloadSearchLogUrl}${encodeURIComponent(searchId)}&redirect=true`, '_blank');
  }
}
